import classNames from "classnames";
import React, { useRef } from "react";
import { IconType } from "react-icons";

import { useResizeObserver } from "@/hooks/useResizeObserver";

import { TableAddRow } from "./TableAddRow";
import { TableRow } from "./TableRow";
import { Column, DataWithId } from "./types";

/**
 * @returns A container for the table that sets the width of the columns
 */
function TableContainer<R extends DataWithId>({
  children,
  columns,
  responsive,
}: {
  children: React.ReactNode;
  columns: Column<R>[];
  responsive?: boolean;
}) {
  const columnWidths = columns.map(({ width, type }) => {
    if (type === "actions") {
      return `auto`;
    }

    return `${width}${responsive ? "fr" : "px"}`;
  });

  return (
    <div
      style={{
        gridTemplateColumns: columnWidths.join(" "),
      }}
      className={classNames("tw-grid tw-overflow-auto", {
        "tw-w-full": responsive,
      })}
    >
      {children}
    </div>
  );
}

function HeaderCellContainer({ children }: { children: React.ReactNode }) {
  return <div className="tw-sticky tw-top-0 tw-bg-neutral-25">{children}</div>;
}

function AccountTableHeaderCell({
  children,
  Icon,
  isIdColumn,
  isRowStart,
  isRowEnd,
}: {
  children: string;
  Icon: IconType;
  isIdColumn?: boolean;
  isRowStart?: boolean;
  isRowEnd?: boolean;
}) {
  return (
    <HeaderCellContainer>
      <div
        className={classNames(
          "tw-box-border tw-flex tw-h-8 tw-items-center tw-gap-1.5 tw-text-nowrap tw-px-2 tw-text-sm tw-font-medium",
          {
            "tw-border-l-solid tw-rounded-l tw-border-l": isRowStart,
            "tw-rounded-r tw-border-r": isRowEnd,
            "tw-mr-1 tw-rounded tw-bg-blue-1 tw-text-blue-4": isIdColumn,
            "tw-border-x-0 tw-border-y tw-border-solid tw-border-neutral-100 tw-bg-neutral-25 tw-text-neutral-300":
              !isIdColumn,
          },
        )}
      >
        <Icon className="tw-h-4 tw-w-4" />
        {children}
      </div>
    </HeaderCellContainer>
  );
}

/**
 * @returns A gap between the last header and the right side of the table
 */
const LastHeaderGap = React.forwardRef<HTMLDivElement>((_, ref) => {
  return (
    <div
      ref={ref}
      className="tw-sticky tw-right-0 tw-top-0 tw-z-10 tw-bg-neutral-25"
    />
  );
});

LastHeaderGap.displayName = "LastHeaderGap";

/**
 * @returns A gap between the header and the first row
 */
function GridRowGap() {
  return <div className="tw-col-span-full tw-h-1 tw-bg-neutral-25" />;
}

const HeaderBottomGap = React.forwardRef<HTMLDivElement>((_, ref) => {
  return (
    <div
      ref={ref}
      className="tw-sticky tw-top-8 tw-z-10 tw-col-span-full tw-h-1 tw-bg-neutral-25"
    />
  );
});

HeaderBottomGap.displayName = "HeaderBottomGap";

export function Table<R extends DataWithId>({
  addRowText,
  columns,
  responsive,
  rows = [],
}: {
  addRowText?: string;
  columns: Column<R>[];
  responsive?: boolean;
  rows?: R[];
}) {
  const lastHeaderGapRef = useRef<HTMLDivElement>(null);
  const headerBottomGapRef = useRef<HTMLDivElement>(null);
  // Get the width of the header to pass to the add row component
  const observedTable = useResizeObserver(headerBottomGapRef.current);
  const observedLastHeaderGap = useResizeObserver(lastHeaderGapRef.current);

  const hasActions = columns[columns.length - 1].type === "actions";
  const actionsColumnWidth = observedLastHeaderGap?.contentRect.width;

  return (
    <TableContainer columns={columns} responsive={responsive}>
      {columns.map(({ title, isRowStart, isRowEnd, type, Icon }) =>
        type === "actions" ? (
          <LastHeaderGap key="actions" ref={lastHeaderGapRef} />
        ) : (
          <AccountTableHeaderCell
            key={title}
            Icon={Icon}
            isIdColumn={type === "id"}
            isRowStart={isRowStart}
            isRowEnd={isRowEnd}
          >
            {title}
          </AccountTableHeaderCell>
        ),
      )}
      <HeaderBottomGap ref={headerBottomGapRef} />
      {addRowText && (
        <TableAddRow
          hasActions={hasActions}
          rightGap={hasActions && actionsColumnWidth ? actionsColumnWidth : 0}
          fullTableWidth={observedTable?.contentRect.width}
        >
          {addRowText}
        </TableAddRow>
      )}
      {rows.map((row, index) => (
        <React.Fragment key={row.id}>
          {(index || addRowText) && <GridRowGap />}
          <TableRow row={row} columns={columns} />
        </React.Fragment>
      ))}
    </TableContainer>
  );
}
